import { Injectable } from '@angular/core';
import { resourceAdaptor } from 'qp-adaptor';
import {
  createRecommendationService,
  RecommendationService as LibraryRecommendationService,
} from 'qp-storefront';
import {
  AspectRatio,
  ContainerVm,
  Resource,
  ResourceList,
} from 'qp-ui-interface';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { ConfigService } from '../../config.service';
import { LocalStorageService } from '../../storage/local-storage.service';
import { StorefrontPolicyModel } from '../storefront-policy/model/StorefrontPolicyModels';
import { StorefrontPolicyService } from '../storefront-policy/storefront-policy.service';
import { getDeviceType } from '../../../utils/deviceUtils';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { ContentType } from 'src/app/shared/enums/contentType';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';

@Injectable({
  providedIn: 'root',
})
export class RecommendationsService {
  url: string | undefined;
  isBrowser = new BehaviorSubject<boolean>(false);
  private recommendationService: LibraryRecommendationService;
  constructor(
    private configService: ConfigService,
    private localStorage: LocalStorageService,
    private storefrontPolicyService: StorefrontPolicyService,
    private readonly newrelicService: NewrelicService
  ) {
    this.recommendationService = createRecommendationService(
      this.configService.applicationConfig.recommendURL
    );
  }

  // detail and player page
  async fetchMoreLikeThis(
    contentId: string,
    contentType: string,
    aspectRatio: AspectRatio
  ): Promise<ContainerVm> {
    try {
      const response = await this.getMoreLikeThis(contentId, contentType);
      return this.getAdaptedData(
        response.resources,
        {
          en: { lang: 'en', n: 'More Like This' },
        },
        aspectRatio
      );
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return null;
    }
  }

  // detail page
  async fetchMostPopular(contentType: ContentType): Promise<ContainerVm> {
    try {
      const response = await this.mostPopular(contentType);
      return this.getAdaptedData(response.resources, {
        en: { lang: 'en', n: 'Popular List' },
      });
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return this.getAdaptedData([], 'details.popularShows');
    }
  }

  // search page
  async fetchTrendingSearch() {
    try {
      const resources = await this.trendingSearch();
      return this.getAdaptedData(resources.resources, 'details.trendingSearch');
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return this.getAdaptedData([], 'details.trendingSearch');
    }
  }

  async fetchBecauseWatched(): Promise<ContainerVm> {
    try {
      const resources = await this.becauseWatched();
      return this.getAdaptedData(
        resources.resources,
        'details.becauseyouWatched'
      );
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return this.getAdaptedData([], 'details.becauseyouWatched');
    }
  }

  private async getMoreLikeThis(
    contentId: string,
    contentType: string
  ): Promise<ResourceList> {
    const type: string[] =
      contentType === ContentType.MOVIE
        ? [ContentType.MOVIE]
        : [ContentType.WEB_SERIES, ContentType.TV_SERIES];

    const request = {
      item: contentId,
      type: 'more-like-this',
      fields: [
        ...this.getDefaultFields(),
        { values: type, bias: 1, name: 'cty' },
      ],
    };
    return this.recommendationService.getRecommendedItems(
      request,
      this.getAccessToken()
    );
  }

  private async mostPopular(contentType: ContentType): Promise<ResourceList> {
    const type: string[] =
      contentType === ContentType.MOVIE
        ? [ContentType.MOVIE]
        : [ContentType.WEB_SERIES, ContentType.TV_SERIES];
    const request = {
      type: 'most-popular',
      fields: [
        ...this.getDefaultFields(),
        { values: type, bias: 1, name: 'cty' },
      ],
    };
    return this.recommendationService.getRecommendedItems(
      request,
      this.getAccessToken()
    );
  }

  private async trendingSearch() {
    const request = {
      type: 'trending-now-1-week',
      fields: [...this.getDefaultFields()],
    };
    return this.recommendationService.getRecommendedItems(
      request,
      this.getAccessToken()
    );
  }

  private async becauseWatched(): Promise<ResourceList> {
    const request = {
      profileId: this.localStorage.getItem(KEYS.SELECTED_PROFILE_ID),
      type: 'because-you-watched',
      fields: this.getDefaultFields(),
    };
    return this.recommendationService.getRecommendedItems(
      request,
      this.getAccessToken()
    );
  }

  private getAccessToken() {
    return this.localStorage.getItem(KEYS.PLATFORM_TOKEN) || '';
  }

  private getDefaultFields() {
    const policy: StorefrontPolicyModel =
      this.storefrontPolicyService.getPolicy();

    return [
      { values: [policy.dt], bias: 1, name: 'dt' },
      { values: [policy.reg], bias: 1, name: 'reg' },
      { values: [policy.acl], bias: 1, name: 'acl' },
      { values: [policy.pf], bias: 1, name: 'pf' },
      { values: [policy.pl], bias: 1, name: 'pl' },
    ];
  }

  private getAdaptedData(
    resources: Resource[],
    localizeName: any,
    aspectRatio: AspectRatio = '2x3'
  ): ContainerVm {
    return {
      id: uuid(),
      name: localizeName.en?.n,
      aspectRatio,
      localizedName: localizeName,
      layout: 'carousel',
      resources: !resources?.length
        ? []
        : resources.map((res: Resource) => {
            const resource = resourceAdaptor(
              this.configService.applicationConfig.imageResizeURL,
              res,
              getDeviceType(),
              aspectRatio === '16x9' ? '0-16x9' : '0-2x3'
            );
            resource.containerName = { en: localizeName?.en?.n };
            resource.containerId = localizeName?.en?.n || '';
            return resource;
          }),
    };
  }
}
